import { PermissionKey } from '../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../services/PermissionService';
import { ColumnMeta, ColumnMenuItem } from '../../types';
import { RESTRICTED_COLUMN_GROUPS } from './constants';

/**
 * Creates a map from column.name to column.path for filtering operations
 * @param columns - Array of ColumnMenuItem objects
 * @returns Map<string, string> where key is column.name and value is column.path
 */
export function createColumnNameToPathMap(columns: ColumnMenuItem[]): Map<string, string> {
  return columns.reduce((map, col) => {
    map.set(col.name, col.path);
    return map;
  }, new Map<string, string>());
}

export function applyPermissionBasedFiltering(columnMeta: ColumnMeta[], columnsToHide: string[] = []) {
  if (!columnMeta || !Array.isArray(columnMeta)) {
    return [];
  }

  const permSvc = PermissionService.get();
  const hasRestrictedColumnAccess = permSvc.hasPermission(PermissionKey.hasRestrictedColumnAccess);

  // Early return if user doesn't have restricted access
  if (!hasRestrictedColumnAccess) {
    return columnMeta;
  }

  // Create a Set of restricted group names for efficient lookup
  const restrictedGroupsArray = [
    RESTRICTED_COLUMN_GROUPS.RUBRICS,
    RESTRICTED_COLUMN_GROUPS.FINANCIALS,
    RESTRICTED_COLUMN_GROUPS.KPIS,
  ] as const;
  const restrictedGroups = new Set<string>(restrictedGroupsArray);

  // Convert individual column names to a set for filtering
  const columnsToHideSet = new Set<string>(columnsToHide.filter((item) => !restrictedGroups.has(item)));

  // Check which restricted groups should be hidden
  const hideRestrictedGroups = new Set<string>(columnsToHide.filter((item) => restrictedGroups.has(item)));

  return columnMeta.filter((column) => {
    // Always hide columns that are explicitly in the columnsToHide list
    if (columnsToHideSet.has(column.name)) {
      return false;
    }

    // For restricted groups, hide if the group is in columnsToHide
    if (hideRestrictedGroups.has(column.groupName)) {
      return false;
    }

    return true;
  });
}

export function shouldHideRestrictedColumnGroups(
  columnGroups: string[],
  columnsToHide: string[]
): Record<string, boolean> {
  const permSvc = PermissionService.get();
  const hasRestrictedColumnAccess = permSvc.hasPermission(PermissionKey.hasRestrictedColumnAccess);
  const columnsToHideSet = new Set(columnsToHide);

  return columnGroups.reduce(
    (acc, group) => {
      acc[group] = hasRestrictedColumnAccess && columnsToHideSet.has(group);
      return acc;
    },
    {} as Record<string, boolean>
  );
}

/**
 * Checks if a field path matches a path to hide, accounting for prefix differences.
 * For example, 'customData.253' should match 'company.customData.253'
 * Optimized to cache string length calculations and minimize operations.
 * @param field - The field path to check (e.g., 'customData.253')
 * @param pathToHide - The path to hide (e.g., 'company.customData.253')
 * @returns true if the field should be hidden
 */
function matchesPathToHide(field: string, pathToHide: string): boolean {
  // Early return for exact match (most common case)
  if (field === pathToHide) {
    return true;
  }

  // Cache string lengths to avoid repeated calculations
  const fieldLen = field.length;
  const pathToHideLen = pathToHide.length;

  // Early return if field is longer than pathToHide (can't be a suffix)
  if (fieldLen > pathToHideLen) {
    // Check if field ends with pathToHide (reverse case)
    // e.g., 'some.prefix.company.customData.253' ends with 'company.customData.253'
    if (field.endsWith(pathToHide)) {
      const index = fieldLen - pathToHideLen;
      return index === 0 || field[index - 1] === '.';
    }
    return false;
  }

  // Check if pathToHide ends with the field path
  // e.g., 'company.customData.253' ends with 'customData.253'
  if (pathToHideLen > fieldLen && pathToHide.endsWith(field)) {
    // Ensure it's a proper suffix (preceded by a dot)
    const index = pathToHideLen - fieldLen;
    return pathToHide[index - 1] === '.';
  }

  return false;
}

export function filterRestrictedColumns(
  fields: string[],
  columnsToHide: string[],
  nameToPathMap: Map<string, string>
): string[] {
  const permSvc = PermissionService.get();
  const hasRestrictedColumnAccess = permSvc.hasPermission(PermissionKey.hasRestrictedColumnAccess);

  if (!hasRestrictedColumnAccess) {
    return fields;
  }

  // Convert column names to paths for filtering
  const pathsToHide = new Set(
    columnsToHide
      .map((name) => nameToPathMap.get(name) || name) // Map name to path, fallback to name if not found
      .filter(Boolean)
  );

  // Early return if no paths to hide
  if (pathsToHide.size === 0) {
    return fields;
  }

  // Two-pass approach for optimal performance:
  // Pass 1: Fast exact match using Set.has() - O(1) per lookup
  // Pass 2: Prefix matching only for fields that didn't match exactly
  const pathsToHideArray = Array.from(pathsToHide);

  return fields.filter((field) => {
    // Pass 1: Check exact match first (O(1) lookup, most common case)
    if (pathsToHide.has(field)) {
      return false; // Hide this field
    }

    // Pass 2: Only check prefix matches for fields that didn't match exactly
    // This avoids expensive string operations for the common exact-match case
    for (const pathToHide of pathsToHideArray) {
      if (matchesPathToHide(field, pathToHide)) {
        return false; // Hide this field
      }
    }

    return true; // Keep this field
  });
}

/**
 * Filters field paths based on restricted column groups.
 * Field paths that start with a restricted group prefix are filtered out.
 * @param fieldPaths - Array of field paths (e.g., ['name', 'KPIs.revenue', 'sector'])
 * @param columnsToHide - Array of column groups to hide (e.g., ['KPIs', 'Rubrics'])
 * @returns Filtered array of field paths
 */
export function filterRestrictedFieldPaths(fieldPaths: string[], columnsToHide: string[]): string[] {
  const permSvc = PermissionService.get();
  const hasRestrictedColumnAccess = permSvc.hasPermission(PermissionKey.hasRestrictedColumnAccess);

  if (!hasRestrictedColumnAccess || !columnsToHide.length) {
    return fieldPaths;
  }

  const columnsToHideSet = new Set(columnsToHide);

  return fieldPaths.filter((fieldPath) => {
    // Check if the field path starts with any restricted column group
    // e.g., 'KPIs.revenue' starts with 'KPIs.'
    for (const restrictedGroup of columnsToHideSet) {
      if (fieldPath.startsWith(`${restrictedGroup}.`)) {
        return false;
      }
    }
    return true;
  });
}
